import React, { useMemo, useState } from 'react';
import { FileText, LogOut, Folder, ArrowUp, Loader2, Plus, Search } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useFolderDashboard } from '../../hooks/useFolderDashboard';
import { getDiagramIcon, getDefaultNativeDiagramIcon } from '../../domains/diagram/models/icons';
import { useThemeMode } from '../../react-app/theme/ThemeProvider';
import { useMeQuery } from '../../hooks/useMeQuery';
import { useLogout } from '../../hooks/useAuth';
import { useDemoStatus } from '../../hooks/useAuth';
import { Input } from '../ui/input';
import {
  Sheet,
  SheetContent,
} from '../ui/sheet';
import { Logger } from '../../utils/logger';
import { useTranslation } from '../../hooks/useTranslation';
import { markLogoutRedirectPending, redirectToLogoutUrl } from '../../utils/logoutRedirect';

const logger = Logger.getLoggerWithPrefix('FolderSidebar');

/** Must match stored names from dashboard onboarding (`getDomainOnboardingDiagramName`). */
const FULL_ORG_CHART_TITLE_SUFFIX = ' - Full Org Chart';
const SUPPORTED_DIAGRAM_TYPES = new Set([
  'generic',
  'flowchart',
  'genogram',
  'org',
  'mindmap',
  'network',
  'database',
  'uml',
]);

interface FolderSidebarProps {
  folderId: string | null;
  open: boolean;
  onClose: () => void;
}

export const FolderSidebar: React.FC<FolderSidebarProps> = ({
  folderId,
  open,
  onClose,
}) => {
  const { t } = useTranslation('diagram');
  const { t: tDashboard } = useTranslation('dashboard');
  const { mode } = useThemeMode();
  const { data, isLoading, canWrite } = useFolderDashboard(folderId || undefined);
  const { data: user } = useMeQuery();
  const demoStatus = useDemoStatus();
  const isDemoUser = useMemo(() => demoStatus?.toUpperCase() === 'DEMO', [demoStatus]);
  const [loadingDiagramId, setLoadingDiagramId] = useState<string | null>(null);
  const [diagramSearchQuery, setDiagramSearchQuery] = useState('');
  const currentDiagramId = (window as any).__DIAGRAM_ID__ as string | undefined;
  const currentDiagramType = useMemo(() => {
    if (!currentDiagramId) return null;
    const rawType = data?.children.diagrams.find((diagram) => diagram.id === currentDiagramId)?.type;
    if (typeof rawType !== 'string') return null;
    const normalizedType = rawType.trim();
    return SUPPORTED_DIAGRAM_TYPES.has(normalizedType) ? normalizedType : null;
  }, [data?.children.diagrams, currentDiagramId]);

  const logoSrc = mode === 'dark'
    ? '/assets/images/logo-dark.svg'
    : '/assets/images/logo-light.svg';

  const filteredDiagrams = useMemo(() => {
    const diagrams = data?.children.diagrams ?? [];
    const query = diagramSearchQuery.trim().toLowerCase();
    if (!query) return diagrams;
    return diagrams.filter((diagram) => diagram.name.toLowerCase().includes(query));
  }, [data?.children.diagrams, diagramSearchQuery]);

  const searchPlaceholder = useMemo(() => {
    const folderName = (
      data?.folder.name ||
      (t('folderSidebar.folderNameFallback', { defaultValue: 'Folder' }) as string)
    ).trim();
    return t('folderSidebar.searchInFolder', {
      folderName,
      defaultValue: `Search diagrams in '${folderName}'`,
    }) as string;
  }, [data?.folder.name, t]);

  const getDiagramDisplayName = (name: string, folderName: string) => {
    const n = (name ?? '').trim();
    const folder = (folderName ?? '').trim();
    if (n.toLowerCase() === 'blank') {
      return t('folderSidebar.blankDiagramDisplay', { defaultValue: 'Blank' }) as string;
    }
    if (n.toLowerCase() === 'untitled diagram') {
      return t('header.untitledDiagram', { defaultValue: n }) as string;
    }
    if (n === 'Demo Org Chart') {
      return tDashboard('domainFolder.demoOrgChartDiagramTitle', { defaultValue: n }) as string;
    }
    if (folder && n === `${folder}${FULL_ORG_CHART_TITLE_SUFFIX}`) {
      return tDashboard('domainFolder.mainOrgChartDiagramTitle', {
        folder,
        defaultValue: n,
      }) as string;
    }
    return name;
  };

  const logoutMutation = useLogout();

  const handleDiagramClick = (diagramId: string) => {
    if (!folderId) return;
    setLoadingDiagramId(diagramId);
    window.location.href = `/f/${folderId}/d/${diagramId}`;
  };

  const handleNewDiagram = () => {
    if (!folderId || !canWrite) return;
    const params = new URLSearchParams();
    if (currentDiagramType) {
      params.set('type', currentDiagramType);
    }
    const query = params.toString();
    window.location.href = query
      ? `/f/${folderId}/d/new?${query}`
      : `/f/${folderId}/d/new`;
  };

  const handleOpenDashboard = () => {
    const dashboardUrl = folderId ? `/dashboard/${folderId}` : '/dashboard';
    const dashboardWindow = window.open(dashboardUrl, 'creately-dashboard');
    if (dashboardWindow) {
      dashboardWindow.focus();
    }
    onClose();
  };

  const handleLogout = async () => {
    if (logoutMutation.isPending) {
      return;
    }

    markLogoutRedirectPending();

    try {
      await window.gravity?.logout?.();
    } catch (error) {
      logger.error('Failed to notify Gravity logout', error);
    }

    try {
      await logoutMutation.mutateAsync();
    } finally {
      redirectToLogoutUrl();
    }
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts.map(p => p[0]).join('').toUpperCase();
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="left" className="w-80 p-0 flex flex-col">
        {/* Logo */}
        <div className="pt-6 px-6 pb-10">
          <img
            src={logoSrc}
            alt="Creately"
            className="h-7"
          />
        </div>

        {/* Tree Navigation */}
        <div className="flex-1 overflow-y-auto px-6">
          {/* Level 0: Dashboard */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="tid.nav.sidebar.dashboard"
                onClick={handleOpenDashboard}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
              >
                <ArrowUp className="h-4 w-4" />
                <span>{t('folderSidebar.dashboard')}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('folderSidebar.dashboard')}</TooltipContent>
          </Tooltip>

          {/* Level 1: Current Folder */}
          <div className="ml-3 mb-1">
            <div className="flex items-center gap-2 py-1.5">
              {(data?.folder as any)?.source === 'native' 
                ? <img src="/assets/app-icons/geno-folder-icon.svg" alt="Folder" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                : <Folder className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
              <h2 className="text-sm font-semibold text-foreground">
                {isLoading ? 'Loading...' : data?.folder.name || 'Folder'}
              </h2>
            </div>

            {/* Level 2: Diagrams */}
            <div className="ml-3 mt-1 space-y-0.5">
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="tid.nav.sidebar.search"
                  value={diagramSearchQuery}
                  onChange={(e) => setDiagramSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-8 pl-8"
                  aria-label={searchPlaceholder}
                />
              </div>

              {/* New Diagram Button - only when user has write access */}
              {canWrite ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      data-testid="tid.nav.sidebar.newDiagram"
                      onClick={handleNewDiagram}
                      className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-accent/50 active:bg-accent transition-colors text-left group"
                    >
                      <Plus className="h-4 w-4 text-primary opacity-70 group-hover:opacity-100 transition-opacity" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-primary font-medium">
                          {t('folderSidebar.newDiagram')}
                        </div>
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('folderSidebar.newDiagram')}</TooltipContent>
                </Tooltip>
              ) : null}

              {isLoading ? (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  {t('folderSidebar.loadingDiagrams', { defaultValue: 'Loading...' })}
                </div>
              ) : filteredDiagrams.length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  {diagramSearchQuery.trim()
                    ? (t('folderSidebar.noMatchingDiagrams', { defaultValue: 'No matching diagrams' }) as string)
                    : (t('folderSidebar.noDiagrams', { defaultValue: 'No diagrams' }) as string)}
                </div>
              ) : (
                filteredDiagrams.map((diagram) => {
                  const iconData = getDiagramIcon(diagram.type) || getDefaultNativeDiagramIcon();
                  const isLoadingThis = loadingDiagramId === diagram.id;
                  const isCurrentDiagram = currentDiagramId === diagram.id;
                  const diagramDisplayName = getDiagramDisplayName(
                    diagram.name,
                    data?.folder.name ?? '',
                  );
                  return (
                    <button
                      key={diagram.id}
                      data-testid={`tid.nav.sidebar.diagram.${diagram.id}`}
                      onClick={() => handleDiagramClick(diagram.id)}
                      disabled={isLoadingThis || isCurrentDiagram}
                      className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors text-left group ${
                        isCurrentDiagram
                          ? 'bg-accent/70 cursor-default'
                          : 'hover:bg-accent/50 active:bg-accent disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {isLoadingThis ? (
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        ) : iconData.path ? (
                          <img
                            src={iconData.path}
                            alt={iconData.title}
                            className={`h-4 w-4 transition-opacity ${
                              isCurrentDiagram ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'
                            }`}
                          />
                        ) : (
                          <FileText className={`h-4 w-4 text-muted-foreground ${
                            isCurrentDiagram ? 'opacity-100' : 'opacity-70'
                          }`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${
                          isCurrentDiagram
                            ? 'text-foreground font-semibold'
                            : 'text-foreground'
                        }`}>
                          {diagramDisplayName}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="mt-auto border-t p-4">
          <div className="flex items-center gap-3">
            {/* User Avatar */}
            {user?.avatarUrl ? (
              <div className="w-10 h-10 rounded-full border-2 border-border overflow-hidden bg-background shadow-sm flex-shrink-0">
                <img
                  src={user.avatarUrl}
                  alt={user.name || 'User'}
                  className="h-full w-full rounded-full object-cover"
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold bg-primary text-primary-foreground shadow-sm flex-shrink-0">
                {user?.name ? getInitials(user.name) : 'U'}
              </div>
            )}

            {/* User Details */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {user?.name || 'User'}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {user?.email || ''}
              </div>
            </div>

            {/* Logout Button */}
            {!isDemoUser && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    data-testid="tid.nav.sidebar.logout"
                    onClick={handleLogout}
                    disabled={logoutMutation.isPending}
                    className="p-2 hover:bg-accent rounded-md transition-colors flex-shrink-0"
                  >
                    <LogOut className="h-4 w-4 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('folderSidebar.logout')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
};
